import React, { useState } from 'react';
import { Star, ChevronDown } from 'lucide-react';
import { BRAND_COLORS } from '../constants/brandColors';
import { formatPriceCadUsd } from '../utils/priceFormat';

/**
 * Collapsible price reveal — click "Découvrir le tarif ✨" to reveal the
 * 3-tier pricing in CAD/USD, click again to collapse.
 *
 * Smooth animation via grid-template-rows 0fr → 1fr (no fixed max-height,
 * adapts to any content height — works on mobile and desktop).
 *
 * variant="package" → 3-line pricing with "Offre lancement -10%" headline
 * variant="alacarte" → 3-line compact pricing
 */
const PriceReveal = ({ pricing, language, cadToUsd, variant = 'package' }) => {
  const [open, setOpen] = useState(false);
  const fmt = (v) => formatPriceCadUsd(v, cadToUsd);

  const toggleLabel = open
    ? (language === 'fr' ? 'Masquer le tarif' : 'Hide pricing')
    : (language === 'fr' ? 'Découvrir le tarif' : 'See pricing');

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="price-reveal-toggle"
        className="w-full flex items-center justify-center gap-2 py-2 text-sm font-semibold uppercase tracking-wider transition-colors duration-200 cursor-pointer"
        style={{ color: BRAND_COLORS.bordeaux, borderTop: `1px solid ${BRAND_COLORS.gold}40` }}
      >
        <span>{toggleLabel}</span>
        <ChevronDown
          className="w-4 h-4 transition-transform duration-300"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      <div
        data-testid="price-reveal-panel"
        style={{
          display: 'grid',
          gridTemplateRows: open ? '1fr' : '0fr',
          transition: 'grid-template-rows 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms ease',
          opacity: open ? 1 : 0
        }}
      >
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div className="pt-4 pb-1">
            {variant === 'package' ? (
              <div className="space-y-3">
                <p className="text-xs line-through" style={{ color: '#999' }}>
                  {language === 'fr' ? 'Prix marché : ' : 'Market price: '}
                  {fmt(pricing.market.visio)} visio · {fmt(pricing.market.presentiel)}{' '}
                  {language === 'fr' ? 'présentiel' : 'in-person'}
                </p>
                <p className="text-sm line-through" style={{ color: '#666' }}>
                  {language === 'fr' ? 'Mon tarif régulier : ' : 'Regular rate: '}
                  {fmt(pricing.regular.visio)} visio · {fmt(pricing.regular.presentiel)}{' '}
                  {language === 'fr' ? 'présentiel' : 'in-person'}
                </p>
                <div className="mt-2">
                  <p
                    className="text-base font-semibold mb-1"
                    style={{ color: BRAND_COLORS.bordeaux }}
                  >
                    <Star className="inline w-4 h-4 mb-1" />{' '}
                    {language === 'fr' ? 'Offre lancement -10%' : 'Launch offer -10%'}
                  </p>
                  <p
                    className="text-2xl lg:text-3xl font-bold leading-tight"
                    style={{ color: BRAND_COLORS.bordeaux }}
                  >
                    {fmt(pricing.launch.visio)} visio · {fmt(pricing.launch.presentiel)}{' '}
                    {language === 'fr' ? 'présentiel' : 'in-person'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <p className="text-xs line-through" style={{ color: '#999' }}>
                  {fmt(pricing.market.visio)} · {fmt(pricing.market.presentiel)}
                </p>
                <p className="text-xs line-through" style={{ color: '#666' }}>
                  {fmt(pricing.regular.visio)} · {fmt(pricing.regular.presentiel)}
                </p>
                <p
                  className="text-base lg:text-lg font-bold leading-tight"
                  style={{ color: BRAND_COLORS.bordeaux }}
                >
                  <Star className="inline w-3 h-3 mb-1" /> {fmt(pricing.launch.visio)} ·{' '}
                  {fmt(pricing.launch.presentiel)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceReveal;
